// Credential-bearing requests must never use the general mutation transport,
// which persists body fingerprints. Keys belong only to the active UI flow.
export { authMutation, authDelete, createAuthRequestKey, emailActionGet } from '../../api/client';

import { ApiError, createAuthRequestKey, getWebInstallationId, type createAccountApi } from '../../api/client';
import type { Envelope, WebYandexReauthChallengeRequest } from '../../api/contract';
import type { components } from '../../generated/api';
import { reauthReturnPath, saveReauthIntent } from './reauthReturn';

export type AccountApi = ReturnType<typeof createAccountApi>;
export type Identity = components['schemas']['UserIdentity'];
export type ReauthStatus = components['schemas']['YandexAuthChallengeStatus'];
export const REAUTH_ROOT = '/auth/web/reauth/yandex/challenges';

export function isTerminalReauthFailure(error: unknown): boolean {
  return error instanceof ApiError && ([401, 403, 410].includes(error.status) || ['AUTH_EPOCH_CHANGED', 'EMAIL_ACTION_CONTEXT_CHANGED'].includes(error.code));
}

export function reauthFailureText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'EMAIL_ACTION_CONTEXT_CHANGED') return 'В другой вкладке открыто другое письмо. Откройте исходное письмо заново.';
    if (error.code === 'AUTH_EPOCH_CHANGED') return 'Аккаунт изменился. Откройте исходное письмо или настройки заново.';
    if (error.status === 0) return 'Не удалось подтвердить результат запроса. Проверьте соединение и повторите то же действие.';
    if (error.status === 401) return 'Сессия или подтверждение больше не действует. Войдите через Яндекс ID и начните действие заново.';
    if (error.status === 403) return 'Не удалось подтвердить этот аккаунт. Используйте тот же аккаунт Яндекс ID и откройте исходное письмо заново.';
    if (error.status === 410) return 'Срок действия подтверждения истёк. Вернитесь к исходному письму или настройкам.';
    if (error.status === 429) return 'Слишком много попыток. Дождитесь окончания паузы.';
    if (error.status === 503) return 'Вход по почте временно недоступен. Яндекс ID продолжает работать.';
  }
  return error instanceof Error ? error.message : 'Не удалось продолжить. Повторите попытку.';
}

export async function startLinkReauth(api: AccountApi, key: string, actionId?: string): Promise<string> {
  reauthReturnPath(actionId);
  const body: WebYandexReauthChallengeRequest = { purpose: 'LINK_EMAIL', installationId: getWebInstallationId(), appVersion: 'kazna-web/0.1.0', ...(actionId ? { actionId } : {}) };
  const result = await api.authMutation<Envelope<components['schemas']['WebYandexAuthChallenge']>>(REAUTH_ROOT, body, key);
  api.assertCurrent();
  const authorization = new URL(result.data.authorizationUrl);
  if (authorization.protocol !== 'https:' || authorization.hostname !== 'oauth.yandex.ru' || authorization.username || authorization.password) throw new Error('Недопустимый адрес Яндекс ID');
  saveReauthIntent({ challengeId: result.data.challengeId, epoch: api.epoch, ...(actionId ? { actionId } : {}) });
  return authorization.href;
}

export async function cancelLinkReauth(api: AccountApi, challengeId: string, key = createAuthRequestKey()): Promise<void> {
  return api.authDelete(`${REAUTH_ROOT}/${challengeId}`, key);
}
